import { type Mesh } from '@/gfx/mesh';

export interface GpuMesh {
  vao: WebGLVertexArrayObject;
  vbo: WebGLBuffer;
  ebo: WebGLBuffer;
  skinVbo: WebGLBuffer | null;
  indexCount: number;
  indexType: number;
  skinned: boolean;
}

const STRIDE_FLOATS = 8;
const STRIDE_BYTES = STRIDE_FLOATS * 4;

// Skin attribute layout per CLAUDE.md §1: 4 bone indices (uint8) then 4 weights (float32).
const SKIN_INDICES_OFFSET = 0;
const SKIN_WEIGHTS_OFFSET = 4;
const SKIN_STRIDE_BYTES = 4 + 16;

const BONE_INDICES_LOC = 9;
const BONE_WEIGHTS_LOC = 10;

export const uploadMesh = (gl: WebGL2RenderingContext, mesh: Mesh): GpuMesh => {
  const vertCount = mesh.positions.length / 3;
  if (mesh.normals.length !== vertCount * 3) {
    throw new Error('mesh positions/normals length mismatch');
  }
  if (mesh.uvs.length !== vertCount * 2) {
    throw new Error('mesh positions/uvs length mismatch');
  }
  const skinned = mesh.boneIndices !== undefined && mesh.boneWeights !== undefined;
  if (skinned) {
    if (mesh.boneIndices!.length !== vertCount * 4) {
      throw new Error('mesh boneIndices length mismatch (expect 4 per vertex)');
    }
    if (mesh.boneWeights!.length !== vertCount * 4) {
      throw new Error('mesh boneWeights length mismatch (expect 4 per vertex)');
    }
  }

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  const ebo = gl.createBuffer();
  if (vao === null || vbo === null || ebo === null) {
    throw new Error('gl buffer/VAO allocation failed');
  }

  const interleaved = new Float32Array(vertCount * STRIDE_FLOATS);
  for (let i = 0; i < vertCount; i++) {
    const dst = i * STRIDE_FLOATS;
    const ps = i * 3;
    const us = i * 2;
    interleaved[dst + 0] = mesh.positions[ps + 0]!;
    interleaved[dst + 1] = mesh.positions[ps + 1]!;
    interleaved[dst + 2] = mesh.positions[ps + 2]!;
    interleaved[dst + 3] = mesh.normals[ps + 0]!;
    interleaved[dst + 4] = mesh.normals[ps + 1]!;
    interleaved[dst + 5] = mesh.normals[ps + 2]!;
    interleaved[dst + 6] = mesh.uvs[us + 0]!;
    interleaved[dst + 7] = mesh.uvs[us + 1]!;
  }

  gl.bindVertexArray(vao);

  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, interleaved, gl.STATIC_DRAW);

  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 3, gl.FLOAT, false, STRIDE_BYTES, 0);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 3, gl.FLOAT, false, STRIDE_BYTES, 12);
  gl.enableVertexAttribArray(2);
  gl.vertexAttribPointer(2, 2, gl.FLOAT, false, STRIDE_BYTES, 24);

  let skinVbo: WebGLBuffer | null = null;
  if (skinned) {
    skinVbo = gl.createBuffer();
    if (skinVbo === null) throw new Error('gl skin vbo allocation failed');
    const skinBuf = new ArrayBuffer(vertCount * SKIN_STRIDE_BYTES);
    const idxView = new Uint8Array(skinBuf);
    const wgtView = new Float32Array(skinBuf);
    for (let i = 0; i < vertCount; i++) {
      const idxBase = i * SKIN_STRIDE_BYTES + SKIN_INDICES_OFFSET;
      idxView[idxBase + 0] = mesh.boneIndices![i * 4 + 0]!;
      idxView[idxBase + 1] = mesh.boneIndices![i * 4 + 1]!;
      idxView[idxBase + 2] = mesh.boneIndices![i * 4 + 2]!;
      idxView[idxBase + 3] = mesh.boneIndices![i * 4 + 3]!;
      const wgtBase = (i * SKIN_STRIDE_BYTES + SKIN_WEIGHTS_OFFSET) / 4;
      wgtView[wgtBase + 0] = mesh.boneWeights![i * 4 + 0]!;
      wgtView[wgtBase + 1] = mesh.boneWeights![i * 4 + 1]!;
      wgtView[wgtBase + 2] = mesh.boneWeights![i * 4 + 2]!;
      wgtView[wgtBase + 3] = mesh.boneWeights![i * 4 + 3]!;
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, skinVbo);
    gl.bufferData(gl.ARRAY_BUFFER, skinBuf, gl.STATIC_DRAW);

    gl.enableVertexAttribArray(BONE_INDICES_LOC);
    gl.vertexAttribIPointer(
      BONE_INDICES_LOC,
      4,
      gl.UNSIGNED_BYTE,
      SKIN_STRIDE_BYTES,
      SKIN_INDICES_OFFSET,
    );
    gl.enableVertexAttribArray(BONE_WEIGHTS_LOC);
    gl.vertexAttribPointer(
      BONE_WEIGHTS_LOC,
      4,
      gl.FLOAT,
      false,
      SKIN_STRIDE_BYTES,
      SKIN_WEIGHTS_OFFSET,
    );
  }

  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ebo);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indices, gl.STATIC_DRAW);

  gl.bindVertexArray(null);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);

  const indexType = mesh.indices instanceof Uint16Array ? gl.UNSIGNED_SHORT : gl.UNSIGNED_INT;
  return {
    vao,
    vbo,
    ebo,
    skinVbo,
    indexCount: mesh.indices.length,
    indexType,
    skinned,
  };
};

export const disposeGpuMesh = (gl: WebGL2RenderingContext, m: GpuMesh): void => {
  gl.deleteVertexArray(m.vao);
  gl.deleteBuffer(m.vbo);
  gl.deleteBuffer(m.ebo);
  if (m.skinVbo !== null) gl.deleteBuffer(m.skinVbo);
};
